/**
 * Turning ticked-off shopping into food in the house.
 *
 * The loop this closes: the list knows what you set out to buy, and once you're
 * home those same things have to be typed in again as inventory. Doing that by
 * hand for a whole shop is the tax the receipt scanner exists to remove — and
 * for anyone who used the list instead, this is the same removal.
 *
 * ## Guessing the category, and admitting it
 *
 * A cart line is a name and nothing else, so the category has to be inferred.
 * The guess is the same vocabulary the vision parser already maps model output
 * through, which means one table serves both and neither drifts. Where it can't
 * tell, it says "other" rather than picking something plausible — a wrong
 * category sends an item to the wrong appliance and gives it the wrong shelf
 * life, and "other" is at least honestly uncertain.
 *
 * Everything lands in the review the same way a scan does: nothing is written
 * until it has been looked at.
 */

import { CATEGORY_RULES } from "../food/categories";
import { estimateExpiryDate } from "../food/expiry";
import type { FoodCategory, FoodItem } from "../food/types";
import { toCategory } from "../vision/parseRecognition";
import type { CartLine } from "./types";

/**
 * Words that place a shopping-list line in a category.
 *
 * Deliberately small and obvious. This is a convenience on top of a guess, not
 * a food ontology, and a long list of clever rules would be a long list of
 * confident mistakes — each one sending something to the wrong shelf with the
 * wrong date on it.
 */
const HINTS: Record<string, FoodCategory> = {
  milk: "dairy", cheese: "dairy", butter: "dairy", yogurt: "dairy",
  yoghurt: "dairy", cream: "dairy", eggs: "dairy", egg: "dairy",
  chicken: "meat", beef: "meat", pork: "meat", lamb: "meat", mince: "meat",
  bacon: "meat", sausages: "meat", fish: "meat", salmon: "meat", prawns: "meat",
  apples: "produce", bananas: "produce", tomatoes: "produce", onions: "produce",
  potatoes: "produce", carrots: "produce", spinach: "produce", lettuce: "produce",
  salad: "produce", peppers: "produce", garlic: "produce", lemons: "produce",
  rice: "pantry", pasta: "pantry", bread: "pantry", flour: "pantry",
  oats: "pantry", beans: "pantry", lentils: "pantry", tins: "pantry",
  peas: "frozen", chips: "frozen", "ice cream": "frozen",
  juice: "drinks", water: "drinks", coffee: "drinks", tea: "drinks",
  beer: "drinks", wine: "drinks",
};

/** The best guess for a bare shopping-list name. "other" when there isn't one. */
export function guessCategory(name: string): FoodCategory {
  const words = name.toLowerCase().replace(/[^a-z\s]/g, " ").split(/\s+/).filter(Boolean);

  for (const word of words) {
    if (HINTS[word]) return HINTS[word];
    // Plurals and possessives are common on a shopping list.
    const singular = word.endsWith("s") ? word.slice(0, -1) : `${word}s`;
    if (HINTS[singular]) return HINTS[singular];
  }

  // The vision parser's vocabulary already knows "vegetable" means produce.
  const mapped = toCategory(words.join(" "));
  return mapped;
}

/**
 * One cart line as an item, filed where its category says it belongs.
 *
 * Quantity is always 1: a list line says *what*, never *how much*, and inventing
 * a number would be a fact the user never gave us. They can change it in the
 * review, where changing it is one tap.
 */
export function lineToItem(line: CartLine, id: string, today: string): FoodItem {
  const category = guessCategory(line.name);
  const rule = CATEGORY_RULES[category];

  return {
    id,
    name: line.name.trim(),
    category,
    location: rule.defaultLocation,
    quantity: 1,
    unit: rule.defaultUnit,
    purchaseDate: today,
    // Through the same estimator the add sheet uses, so a stowed item and a
    // typed one get the same date for the same food.
    estimatedExpiryDate: estimateExpiryDate(category, rule.defaultLocation, today),
  };
}
